import {
  Block,
  Delete,
  Edit,
  Email,
  KeyOutlined,
  LockOpen,
  PowerSettingsNew,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import {
  ACTIVATE_USER,
  BLOCK_USER,
  DELETE_USER,
  RECIPIENTS_LIST_FETCH,
  UNBLOCK_USER,
} from "../../../../enums/ApiPaths";
import {
  EDIT_CLIENT_ACTIVATION_STATUS,
  EDIT_EMPLOYEE_ACTIVATION_STATUS,
  SEND_INBOX,
} from "../../../../enums/PermissionTitles";
import { deleteCore } from "../../../../enums/queryFunction/Delete";
import {
  PERMISSIONS,
  PERMISSIONS_EDIT,
  USERS_CLINIC_SINGLE,
} from "../../../../enums/QueryKeys";
import { ADMIN, EMPLOYEE, MASTER, USER } from "../../../../enums/Roles";
import {
  CHANGE_FORGOTTEN_PASSWORD,
  CLINIC_USERS,
} from "../../../../enums/UrlPaths";
import { useFetch } from "../../../../hooks/Fetching";
import usePut from "../../../../hooks/usePut";
import useUser, { useGetUserId } from "../../../../hooks/useUser";
import { useIsMobile } from "../../../../utils/Functions";
import NewConversation from "../../../inbox/form/NewConversation";
import ActionRow from "../../clinic/components/actions/ActionRow";
import DeleteUserDialogContent from "./DeleteUserDialogContent";

function canView(loggedInUserRole, viewedUserRole, viewedUserEmail) {
  if (loggedInUserRole == MASTER) return true;
  if (loggedInUserRole == USER) return false;
  if (viewedUserRole == EMPLOYEE) return true;
  if (viewedUserRole == USER && loggedInUserRole != MASTER) {
    return viewedUserEmail;
  } 
  return false;
}

export default function UserActionsMenu({
  email,
  userId,
  active,
  blocked,
  role,
  onEdit = null,
  canEdit = false,
  editLabel = "Edit",
  editDescription = "",
  showResetPassword = false,
  showOpenConversation = false,
  menuTitle = "EmployeeActions",
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {profile} = useUser();
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const isCanView = canView(profile.role, role, email);
  // Inactive clients created by the clinic often have no email, but must still be editable
  const isEditAllowed = Boolean(canEdit && onEdit);

  if (!isCanView && !isEditAllowed) return;

  return (
    <>
      <IconButton
        title={t("EmployeeActions")}
        onClick={() => setActionsOpen(true)}
        data-testid="user.actions-menu.trigger"
        sx={{
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: "scale(1.05)",
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Dialog
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 8 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t(menuTitle)}
          </Typography>
          <IconButton
            onClick={() => setActionsOpen(false)}
            color="error"
            title={t("Close")}
            data-testid="user.actions-menu.close-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.6 }} />
        <DialogContent
          sx={{
            p: 0,
            // Automatically adds a dividing line ONLY between rendered components
            "& > div + div": {
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <EditUserAction
            onEdit={onEdit}
            canEdit={canEdit}
            editLabel={editLabel}
            editDescription={editDescription}
            setActionsOpen={setActionsOpen}
          />
          {isCanView ? (
            <>
              {email ? <ConversationAction
                userId={userId}
                showOpenConversation={showOpenConversation}
                setActionsOpen={setActionsOpen}
              /> : null}
              <ResetPasswordAction
                userId={userId}
                showResetPassword={showResetPassword}
                setActionsOpen={setActionsOpen}
              />
              <ActivationAction userId={userId} active={active} role={role} />
              <BlockAction userId={userId} blocked={blocked} role={role} />
              <DeleteUserAction
                userId={userId}
                role={role}
                setActionsOpen={setActionsOpen}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
const EditUserAction = ({
  onEdit,
  canEdit,
  editLabel,
  editDescription,
  setActionsOpen,
}) => {
  const { t } = useTranslation();
  if (!canEdit || !onEdit) return null;

  return (
    <Box>
      <ActionRow
        icon={<Edit />}
        label={t(editLabel)}
        description={editDescription ? t(editDescription) : ""}
        testId="user.actions-menu.edit-button"
        onClick={() => {
          setActionsOpen(false);
          onEdit();
        }}
      />
    </Box>
  );
};

const ConversationAction = ({
  userId,
  showOpenConversation,
  setActionsOpen,
}) => {
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { roles } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversationOpen, setConversationOpen] = React.useState(false);
  const fullScreenConversation = useIsMobile();

  const canSendInbox = isPermissionAllowed(SEND_INBOX) && !roles.user;

  const {
    data: users = [],
    isLoading,
    error,
  } = useFetch(
    RECIPIENTS_LIST_FETCH,
    null,
    "recipients" + userId,
    conversationOpen && canSendInbox,
  );

  if (!showOpenConversation || !canSendInbox) return null;

  const handleClose = () => {
    setConversationOpen(false);
    searchParams.delete("receiver");
    searchParams.delete("receiver_ids");
    setSearchParams(searchParams);
  };

  return (
    <Box>
      <ActionRow
        icon={<Email />}
        label={t("SendMessage")}
        description={t("OpenConversationDescription")}
        onClick={() => {
          setConversationOpen(true);
        }}
        testId="user.actions-menu.message-button"
      />
      <Dialog
        open={conversationOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreenConversation}
      >
        <NewConversation
          error={error}
          isLoading={isLoading}
          users={users}
          profileId={userId}
          handleClose={handleClose}
        />
      </Dialog>
    </Box>
  );
};

const ResetPasswordAction = ({ userId, showResetPassword, setActionsOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!showResetPassword) return null;

  return (
    <Box>
      <ActionRow
        icon={<KeyOutlined />}
        label={t("ResetPassword")}
        description={t("SendUserAPasswordResetLink")}
        onClick={() => {
          setActionsOpen(false);
          navigate(CHANGE_FORGOTTEN_PASSWORD, { state: { userId } });
        }}
      />
    </Box>
  );
};

const ActivationAction = ({ userId, active, role }) => {
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { roles } = useUser();
  const loggedInUserId = useGetUserId();
  const queryClient = useQueryClient();

  const activation = usePut(ACTIVATE_USER + userId, PERMISSIONS + userId);

  const isSelf = Number(loggedInUserId) === Number(userId);
  const isEmployeeViewingAdmin =
    roles?.employee && String(role).toUpperCase() === ADMIN;
  const isTargetUserRole = String(role).toUpperCase() === USER;

  // Deactivating a client and toggling a staff member's access are different concerns,
  // independently permission-gated.
  const hasActivationPermission = isTargetUserRole
    ? isPermissionAllowed(EDIT_CLIENT_ACTIVATION_STATUS)
    : isPermissionAllowed(EDIT_EMPLOYEE_ACTIVATION_STATUS);

  const canToggleActivation =
    !isSelf &&
    !isEmployeeViewingAdmin &&
    (hasActivationPermission || roles.master);

  if (!canToggleActivation) return null;

  const handleActivation = async () => {
    await activation.submitCallback();
    queryClient.invalidateQueries([PERMISSIONS_EDIT + userId]);
  };

  return (
    <Box>
      <ActionRow
        icon={<PowerSettingsNew />}
        label={active ? t("DeactivateUser") : t("ActivateUser")}
        description={
          active ? t("DeactivateUserDescription") : t("ActivateUserDescription")
        }
        onClick={handleActivation}
        loading={activation.loading}
        disabled={activation.loading}
        color={active ? "error" : "success"}
        testId="user.actions-menu.activation-button"
      />
    </Box>
  );
};

const BlockAction = ({ userId, blocked, role }) => {
  const { t } = useTranslation();
  const { roles } = useUser();
  const queryClient = useQueryClient();

  // A ban is master-only (admins cannot ban anyone), and only ever applies to a client
  // account - it's a separate, stronger action than deactivation, meant to survive both
  // login and re-registration attempts until a master explicitly lifts it.
  const isTargetUserRole = String(role).toUpperCase() === USER;
  const block = usePut(BLOCK_USER + userId, USERS_CLINIC_SINGLE + userId);
  const unblock = usePut(UNBLOCK_USER + userId, USERS_CLINIC_SINGLE + userId);

  if (!roles.master || !isTargetUserRole) return null;

  const action = blocked ? unblock : block;

  const handleToggle = async () => {
    await action.submitCallback();
    queryClient.invalidateQueries([USERS_CLINIC_SINGLE + userId]);
  };

  return (
    <Box>
      <ActionRow
        icon={blocked ? <LockOpen /> : <Block />}
        label={blocked ? t("UnblockUser") : t("BlockUser")}
        description={blocked ? t("UnblockUserDescription") : t("BlockUserDescription")}
        onClick={handleToggle}
        loading={action.loading}
        disabled={action.loading}
        color={blocked ? "success" : "error"}
        testId="user.actions-menu.block-button"
      />
    </Box>
  );
};

const DeleteUserAction = ({ userId, role, setActionsOpen }) => {
  const { t } = useTranslation();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { roles } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");

  const isTargetAdminRole = String(role).toUpperCase() === ADMIN;
  const isTargetUserRole = String(role).toUpperCase() === USER;
  
  const canDelete =
    (roles.master || roles.admin) &&
    !isTargetAdminRole &&
    !(roles.admin && isTargetUserRole);

  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteCore(DELETE_USER, userId, { confirmationText: confirmText }),
    retry: false,
    onSuccess: () => {
      queryClient.setQueryData(["clinicUsers"], (old) =>
        old ? old.filter((u) => Number(u.id) !== Number(userId)) : old,
      );
      queryClient.invalidateQueries({ queryKey: ["clinicUsers"] });
      handleSnackbar("successMessage", "success");
      navigate(-1);
    },
    onError: () => handleSnackbar("errorMessage", "error"),
  });

  if (!canDelete) return null;

  return (
    <Box>
      <ActionRow
        icon={<Delete />}
        label={t("DeleteUser")}
        description={t("PermanentlyDeleteUser")}
        onClick={() => {
          setConfirmText("");
          setDeleteOpen(true);
        }}
        color="error"
        testId="user.actions-menu.delete-button"
      />
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DeleteUserDialogContent
          confirmText={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={() => {
            setDeleteOpen(false);
            deleteMutation.mutate(confirmText);
          }}
          isLoading={deleteMutation.isLoading}
        />
      </Dialog>
    </Box>
  );
};
